import * as React from 'react';

import { replaceDocumentPlaceholdersWithLinks } from '@/utils/documentLinks';
import {
  buildDocumentTemplate,
  escapeHtml,
  stripScripts,
  wrapHtmlContent,
  wrapPlainTextContent,
} from '@/utils/htmlDocumentTemplate';

type PrintOptions = {
  title?: string;
  getHtml?: () => string;
  organizationId?: string;
};

type PrintTarget = {
  windowRef: Window;
};

const openHostWindow = (): Window | null => {
  const attempts: Array<() => Window | null> = [
    () => window.open('about:blank', '_blank'),
    () => window.open('', '_blank'),
    () => window.open('', '_blank', 'noopener=yes'),
  ];

  for (const attempt of attempts) {
    const win = attempt();

    if (win) {
      try {
        (win as Window).opener = null;
      } catch {
        // ignore
      }

      return win;
    }
  }

  return null;
};

const openPrintTarget = (): PrintTarget | null => {
  const hostWindow = openHostWindow();

  if (!hostWindow) {
    if (typeof window !== 'undefined') {
      return null;
    }

    return null;
  }

  const hostDoc = hostWindow.document;

  hostDoc.open();
  hostDoc.write(
    '<!doctype html><html><head><meta charset="utf-8"/><title>Подготовка печати</title><style>body{margin:0;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;background:#f8fafc;color:#475569;display:flex;align-items:center;justify-content:center;height:100vh;} .message{background:#fff;padding:16px 24px;border-radius:8px;box-shadow:0 4px 20px rgba(15,23,42,0.08);}</style></head><body><div class="message">Подготовка печати…</div></body></html>',
  );
  hostDoc.close();

  return { windowRef: hostWindow };
};

export const usePrint = (getPlainText: () => string, options?: PrintOptions) => {
  return React.useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const plainText = getPlainText();
    const htmlGetter = options?.getHtml;
    const rawHtml = htmlGetter?.() ?? '';
    const htmlWithLinks =
      rawHtml && options?.organizationId
        ? replaceDocumentPlaceholdersWithLinks(rawHtml, options.organizationId)
        : rawHtml;
    const safeHtml = htmlWithLinks.trim() ? stripScripts(htmlWithLinks) : '';

    if (!plainText && !safeHtml) {
      return;
    }

    const bodyContent = safeHtml ? wrapHtmlContent(safeHtml) : wrapPlainTextContent(plainText);

    const target = openPrintTarget();

    if (!target) {
      return;
    }

    const printWindow = target.windowRef;
    const title = escapeHtml(options?.title ?? 'Документ');
    const html = buildDocumentTemplate(title, bodyContent);
    const { document: printDocument } = printWindow;

    printDocument.open();
    printDocument.write(html);
    printDocument.close();

    const handleReady = () => {
      let isClosed = false;
      const cleanup = () => {
        if (isClosed) {
          return;
        }
        isClosed = true;
        printWindow.close();
        printWindow.removeEventListener('afterprint', cleanup);
      };

      const printNow = () => {
        try {
          printWindow.focus();
        } catch {
          // ignore focus issues
        }

        let printFailed = false;

        try {
          printWindow.print();
        } catch {
          printFailed = true;
        }

        printWindow.addEventListener('afterprint', cleanup);
        // Keep a fallback close for browsers that do not emit `afterprint`.
        setTimeout(cleanup, printFailed ? 0 : 1500);
      };

      if ('requestAnimationFrame' in printWindow) {
        printWindow.requestAnimationFrame(() => {
          printNow();
        });
      } else {
        printNow();
      }
    };

    if (printDocument.readyState === 'complete') {
      handleReady();
    } else {
      printWindow.addEventListener('load', handleReady, { once: true });
    }
  }, [getPlainText, options?.title, options?.getHtml, options?.organizationId]);
};
